import axios from 'axios';
import { getTokens } from '../auth.service';

const environmentUrl = import.meta.env.VITE_AUTH_BASE_URL;

const getToken = () => {
  return localStorage.getItem('Authorization');
};

const getRefreshToken = () => {
  return localStorage.getItem('RefreshToken');
};

const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem('Authorization', accessToken);
  if (refreshToken) {
    localStorage.setItem('RefreshToken', refreshToken);
  }
};

export const clearTokens = () => {
  localStorage.removeItem('Authorization');
  localStorage.removeItem('RefreshToken');
  localStorage.removeItem('user_id');
};

// Axios instance for backend API calls
export const authAxios = axios.create({
  baseURL: environmentUrl,
  timeout: 300000,
  withCredentials: true,
});

// Request interceptor to add token to every request
authAxios.interceptors.request.use(
  config => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor
let isRefreshing = false;
let refreshPromise = null;

authAxios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // If there is no config, cannot retry → return error
    if (!originalRequest) {
      return Promise.reject(error);
    }

    const status = error.response?.status;

    // correct condition
    const isTokenError = (status === 401 || status === 400) && !originalRequest._retry;

    if (isTokenError) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          clearTokens();
          window.location.href = '/auth';
          return Promise.reject(error);
        }

        refreshPromise = axios
          .get(`${environmentUrl}/Auth/v1.0/refresh-token`, {
            headers: { Authorization: `Bearer ${refreshToken}` },
          })
          .then(async response => {
            const { data } = response.data;

            const tokenResponse = await getTokens({ authcode: data.authcode });
            const { accessToken, refreshToken: newRefreshToken } = tokenResponse.data.data;

            setTokens(accessToken, newRefreshToken);
            return accessToken;
          })
          .catch(refreshError => {
            clearTokens();
            window.location.href = '/auth';
            throw refreshError;
          })
          .finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });
      }

      try {
        const newToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return authAxios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
